/**
 * Client-side logger for browser environments.
 *
 * In development: outputs to console with module context prefix.
 * In production: debug/info are silent; warn/error go to the console AND are
 * reported to POST /api/client-errors (fire-and-forget, throttled, deduped)
 * so production client failures are visible server-side instead of dying in
 * the user's devtools. Until 2026-08-02 this module returned a full no-op in
 * production — which silently disabled every error boundary's reporting.
 *
 * Usage:
 *   import { clientLogger } from '@/lib/client-logger'
 *   const log = clientLogger('FlightScheduler')
 *   log.debug('Loading flights', { count: 5 })
 *   log.error('Failed to save', { err })
 */

const isProduction = process.env.NODE_ENV === 'production'

interface ClientLog {
  debug: (...args: unknown[]) => void
  info: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
}

const noop = () => {}

// ---------------------------------------------------------------------------
// Production error reporting
// ---------------------------------------------------------------------------

/** Hard cap on reports per page lifetime — a render-loop crash must not DoS
 * our own ingest endpoint (or burn the user's mobile data). */
const MAX_REPORTS_PER_PAGE = 20
/** Identical messages within this window are sent once. */
const DEDUPE_WINDOW_MS = 60_000

let reportCount = 0
const recentReports = new Map<string, number>()

/** Test hook: reset throttle/dedupe state. */
export function _resetClientReporting(): void {
  reportCount = 0
  recentReports.clear()
}

/**
 * Render one log argument into a string that survives JSON transport.
 * Errors keep name/message and a truncated stack; objects are JSON-rendered
 * (best-effort — circular structures degrade to String()).
 */
function serializeArg(arg: unknown): string {
  if (arg instanceof Error) {
    const stack = arg.stack ? `\n${arg.stack.split('\n').slice(0, 8).join('\n')}` : ''
    return `${arg.name}: ${arg.message}${stack}`
  }
  if (typeof arg === 'string') return arg
  try {
    return JSON.stringify(arg)
  } catch {
    return String(arg)
  }
}

function reportToServer(module: string, level: 'warn' | 'error', args: unknown[]): void {
  try {
    if (typeof window === 'undefined' || typeof fetch !== 'function') return
    if (reportCount >= MAX_REPORTS_PER_PAGE) return

    const message = args.map(serializeArg).join(' ').slice(0, 4000)

    const now = Date.now()
    const dedupeKey = `${module}:${message.slice(0, 200)}`
    const lastSent = recentReports.get(dedupeKey)
    if (lastSent !== undefined && now - lastSent < DEDUPE_WINDOW_MS) return
    recentReports.set(dedupeKey, now)
    reportCount++

    const body = JSON.stringify({
      module: module.slice(0, 100),
      level,
      message,
      url: window.location?.href?.slice(0, 500) ?? null,
      userAgent: navigator?.userAgent?.slice(0, 300) ?? null,
    })

    // keepalive lets the report survive page unload (same reason the
    // performance-metrics beacon uses it). Fire-and-forget: reporting must
    // never affect the code path that failed.
    void fetch('/api/client-errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(noop)
  } catch {
    // Reporting must never throw into the caller.
  }
}

// ---------------------------------------------------------------------------
// Logger factory
// ---------------------------------------------------------------------------

export function clientLogger(module: string): ClientLog {
  const prefix = `[${module}]`

  if (isProduction) {
    return {
      debug: noop,
      info: noop,
      warn: (...args: unknown[]) => {
        console.warn(prefix, ...args) // eslint-disable-line no-console
        reportToServer(module, 'warn', args)
      },
      error: (...args: unknown[]) => {
        console.error(prefix, ...args) // eslint-disable-line no-console
        reportToServer(module, 'error', args)
      },
    }
  }

  return {
    debug: (...args: unknown[]) => console.log(prefix, ...args),    // eslint-disable-line no-console
    info: (...args: unknown[]) => console.info(prefix, ...args),    // eslint-disable-line no-console
    warn: (...args: unknown[]) => console.warn(prefix, ...args),    // eslint-disable-line no-console
    error: (...args: unknown[]) => console.error(prefix, ...args),  // eslint-disable-line no-console
  }
}
